import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { emailDoUsuario, supabase } from "./supabase";
import type { AcaoPedidoManual } from "./pedidosManuais";

export type Papel = "admin" | "lider" | "separador" | "conferente" | "estoquista" | "expedidor" | "cx";

export interface Perfil {
  id: string;
  nome: string;
  usuario: string | null;
  papel: Papel;
  papeis_extras: Papel[];
  ativo: boolean;
  deposito_padrao_id: string | null;
  /** áreas a mais da função (tela Usuários, aba Funções); carregado junto com o perfil */
  areas?: Papel[];
  /** telas que a função esconde do menu (rota do ItemMenu) */
  telas_ocultas?: string[];
  acoes_pedidos_manuais?: AcaoPedidoManual[];
  pedidos_manuais_personalizado?: boolean;
}

interface Contexto {
  sessao: Session | null;
  perfil: Perfil | null;
  carregando: boolean;
  /** a sessão não carregou em 8 s (Supabase do computador fora do ar ou travado) */
  semConexao: boolean;
  entrar: (usuario: string, senha: string) => Promise<string | null>;
  sair: () => Promise<void>;
}

const AuthContext = createContext<Contexto | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [sessao, setSessao] = useState<Session | null>(null);
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [semConexao, setSemConexao] = useState(false);

  const carregarPerfil = useCallback(async (s: Session | null) => {
    if (!s) {
      setPerfil(null);
      return;
    }
    const { data } = await supabase
      .from("perfis")
      .select("id, nome, usuario, papel, papeis_extras, ativo, deposito_padrao_id")
      .eq("id", s.user.id)
      .maybeSingle();
    const p = (data as Perfil | null) ?? null;
    if (p && p.papel !== "admin") {
      const { data: funcoes } = await supabase.from("funcao_acessos").select("papel, areas, telas_ocultas")
        .in("papel", [p.papel, ...(p.papeis_extras ?? [])]);
      const lista = (funcoes ?? []) as Array<{ papel: Papel; areas: Papel[]; telas_ocultas: string[] }>;
      p.areas = [...new Set(lista.flatMap((f) => f.areas ?? []))];
      p.telas_ocultas = lista.find((f) => f.papel === p.papel)?.telas_ocultas ?? [];
    }
    if (p) {
      const { data: acessos, error } = await supabase.rpc("wms_pedido_manual_acessos", {});
      p.acoes_pedidos_manuais = !error && acessos?.ok ? acessos.acoes : [];
      p.pedidos_manuais_personalizado = !error && acessos?.ok ? acessos.personalizado : false;
    }
    setPerfil(p);
  }, []);

  useEffect(() => {
    let ativo = true;
    // com o Supabase fora do ar a renovação do token fica tentando sem fim: em 8 s a tela
    // explica o que fazer, e se o Supabase voltar a sessão carrega sozinha
    const limite = setTimeout(() => { if (ativo) setSemConexao(true); }, 8000);
    supabase.auth.getSession()
      .then(async ({ data }) => {
        if (!ativo) return;
        setSessao(data.session);
        await carregarPerfil(data.session);
      })
      .catch(() => undefined)
      .finally(() => {
        clearTimeout(limite);
        if (!ativo) return;
        setSemConexao(false);
        setCarregando(false);
      });
    const { data: sub } = supabase.auth.onAuthStateChange((evento, s) => {
      setSessao(s);
      if (evento === "SIGNED_IN" || evento === "SIGNED_OUT" || evento === "USER_UPDATED") {
        void carregarPerfil(s);
      }
    });
    return () => {
      ativo = false;
      clearTimeout(limite);
      sub.subscription.unsubscribe();
    };
  }, [carregarPerfil]);

  const entrar = useCallback(async (usuario: string, senha: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email: emailDoUsuario(usuario), password: senha });
    if (error) {
      if (error.message.includes("Invalid")) return "Usuário ou senha incorretos";
      if (/fetch|network|Load failed|JSON/i.test(error.message) || error.status === 0 || (error.status ?? 0) >= 500) {
        return "Sem conexão com o Supabase. Confira se o iniciar-wms.bat está aberto e o Docker Desktop mostra Engine running.";
      }
      return error.message;
    }
    await carregarPerfil(data.session);
    return null;
  }, [carregarPerfil]);

  const sair = useCallback(async () => {
    await supabase.auth.signOut();
    setPerfil(null);
  }, []);

  return (
    <AuthContext.Provider value={{ sessao, perfil, carregando, semConexao, entrar, sair }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): Contexto {
  const c = useContext(AuthContext);
  if (!c) throw new Error("useAuth fora do AuthProvider");
  return c;
}

/** admin passa em qualquer checagem; as áreas extras da pessoa e as áreas a mais da função também contam */
export function temPapel(perfil: Perfil | null, ...papeis: Papel[]): boolean {
  if (!perfil || !perfil.ativo) return false;
  return perfil.papel === "admin" || papeis.includes(perfil.papel)
    || (perfil.papeis_extras ?? []).some((p) => papeis.includes(p))
    || (perfil.areas ?? []).some((p) => papeis.includes(p));
}

export const nomePapel: Record<Papel, string> = {
  admin: "Admin",
  lider: "Líder",
  separador: "Separador",
  conferente: "Conferente",
  estoquista: "Estoquista",
  expedidor: "Expedidor",
  cx: "Atendimento (CX)",
};
